import { ToastManagerRepository } from "@core/domain/repositories/toast-manager.repository";
import type { Undefinable } from "@core/domain/types/undefinable.type";
import type { PlotLayerData } from "@core/presentation/components/maps/map-layers-control";
import type { Farm } from "@farm/domain/models/farm.model";
import type { LayerType } from "@farm/domain/models/layer-type.model";
import type { Plot, PlotProps } from "@farm/domain/models/plot.model";
import { FarmAddPlotUseCase } from "@farm/domain/usecases/farm-add-plot.usecase";
import { FarmGetByIdUseCase } from "@farm/domain/usecases/farm-get-by-id.usecase";
import { LayerFetchUseCase } from "@farm/domain/usecases/layer-fetch.usecase";
import { LayerTypesGetAllUseCase } from "@farm/domain/usecases/layer-types-get-all.usecase";
import { PlotDeleteUseCase } from "@farm/domain/usecases/plot-delete.usecase";
import { BaseViewModel } from "@iot-platform/core";
import { t } from "i18next";
import { inject, injectable } from "inversify";
import {
    action,
    computed,
    makeObservable,
    observable,
    runInAction,
} from "mobx";

export type PlotProperties = GeoJSON.GeoJsonProperties & { plot: PlotProps };

interface PlotData {
    plotId: string;
    geometry: GeoJSON.Geometry;
}

@injectable()
export class FarmMapViewmodel extends BaseViewModel {
    @observable
    plotsGeoJson: GeoJSON.FeatureCollection<
        GeoJSON.Geometry,
        PlotProperties
    > | null = null;

    @observable
    plotBounds: [number, number][] = [];

    @observable
    selectedPlot: Undefinable<Readonly<Plot>> = undefined;

    @observable
    showEditPlotModal = false;

    @observable
    layerTypes: LayerType[] = [];

    @computed
    get plotsData(): PlotData[] {
        if (!this.plotsGeoJson) return [];

        return this.plotsGeoJson.features.map((feature) => {
            const plot = feature.properties.plot;
            // Deep copy geometry to avoid reactive access later
            return {
                plotId: plot.id.value,
                geometry: JSON.parse(
                    JSON.stringify(feature.geometry),
                ) as GeoJSON.Geometry,
            };
        });
    }

    constructor(
        @inject(FarmAddPlotUseCase)
        private readonly farmAddPlotUseCase: FarmAddPlotUseCase,
        @inject(FarmGetByIdUseCase)
        private readonly farmGetByIdUseCase: FarmGetByIdUseCase,
        @inject(PlotDeleteUseCase)
        private readonly plotDeleteUseCase: PlotDeleteUseCase,
        @inject(LayerTypesGetAllUseCase)
        private readonly layerTypesGetAllUseCase: LayerTypesGetAllUseCase,
        @inject(LayerFetchUseCase)
        private readonly layerFetchUseCase: LayerFetchUseCase,
    ) {
        super();
        makeObservable(this);
    }

    @action
    showFarm(farm: Readonly<Farm>): void {
        const features: GeoJSON.Feature<GeoJSON.Geometry, PlotProperties>[] =
            [];

        const bounds: [number, number][] = [];

        for (const plot of farm.plots) {
            if (this.selectedPlot?.id.value === plot.id.value) {
                this.selectedPlot = plot;
            }

            const parsed = JSON.parse(plot.geoJson) as
                GeoJSON.FeatureCollection | GeoJSON.Feature | GeoJSON.Polygon;

            if (parsed.type === "FeatureCollection") {
                for (const feature of parsed.features) {
                    const featureWithPlot: GeoJSON.Feature<
                        GeoJSON.Geometry,
                        PlotProperties
                    > = {
                        ...feature,
                        properties: {
                            ...(feature.properties ?? {}),
                            plot,
                        },
                    };
                    features.push(featureWithPlot);
                    bounds.push(...this.extractCoords(feature.geometry));
                }
            } else if (parsed.type === "Feature") {
                const featureWithPlot: GeoJSON.Feature<
                    GeoJSON.Geometry,
                    PlotProperties
                > = {
                    ...parsed,
                    properties: {
                        ...(parsed.properties ?? {}),
                        plot,
                    },
                };
                features.push(featureWithPlot);
                bounds.push(...this.extractCoords(parsed.geometry));
            } else {
                const featureWithPlot: GeoJSON.Feature<
                    GeoJSON.Polygon,
                    PlotProperties
                > = {
                    type: "Feature",
                    geometry: parsed,
                    properties: { plot },
                };
                features.push(featureWithPlot);
                bounds.push(...this.extractCoords(parsed));
            }
        }
        this.plotsGeoJson = { type: "FeatureCollection", features };
        this.plotBounds = bounds;
    }

    @action
    clearFarm(): void {
        this.plotsGeoJson = null;
        this.plotBounds = [];
    }

    @action
    async addPlot(
        farmId: number,
        geojson: GeoJSON.Feature<GeoJSON.Polygon>,
    ): Promise<void> {
        const geometry = geojson.geometry;

        const plot = await this.farmAddPlotUseCase.execute(farmId, geometry);
        if (!plot) return;

        await this.reloadFarm(farmId);
    }

    @action
    async deletePlot(farmId: number, plotId: string): Promise<void> {
        const plotDeleted = await this.plotDeleteUseCase.execute(
            farmId,
            plotId,
        );
        if (!plotDeleted) return;

        this.clearFarm();
        await this.reloadFarm(farmId);
    }

    private extractCoords(geometry: GeoJSON.Geometry): [number, number][] {
        if (geometry.type === "Polygon") {
            return (geometry.coordinates[0] as [number, number][]).map(
                ([lng, lat]) => [lat, lng],
            );
        }
        if (geometry.type === "MultiPolygon") {
            return (
                (
                    geometry.coordinates[0]?.[0] as
                        [number, number][] | undefined
                )?.map(([lng, lat]) => [lat, lng]) ?? []
            );
        }
        return [];
    }

    @action
    async reloadFarm(farmId: number): Promise<void> {
        const farm = await this.farmGetByIdUseCase.execute(farmId);
        if (farm) {
            this.showFarm(farm);
        }
    }

    @action
    toggleShowEditPlotModal(): void {
        this.showEditPlotModal = !this.showEditPlotModal;
    }

    @action
    setSelectedPlot(plot: PlotProps): void {
        this.selectedPlot = plot;
        this.toggleShowEditPlotModal();
    }

    @computed
    get layerTypesBySource(): Map<string, LayerType[]> {
        const grouped = new Map<string, LayerType[]>();
        for (const layerType of this.layerTypes) {
            const source = layerType.source;
            const existing = grouped.get(source) ?? [];
            existing.push(layerType);
            grouped.set(source, existing);
        }
        return grouped;
    }

    @action
    async fetchLayerTypes(sources: string[]): Promise<void> {
        const layerTypes = await this.layerTypesGetAllUseCase.execute(sources);
        runInAction(() => {
            this.layerTypes = layerTypes;
        });
    }

    async getLayerImages(
        farmId: string,
        layerCode: string,
        weeksAgo: number,
        source: string,
    ): Promise<PlotLayerData[]> {
        const plotDataList = runInAction(() => this.plotsData);

        if (plotDataList.length === 0) return [];

        const baseUrl =
            window.__BRANDING_ENV__.VITE_IOT_ADMIN_API_SERVER_URL ?? "";

        // Execute all requests in parallel
        const layerPromises = plotDataList.map(async (plotData) => {
            // Extract polygon bounds
            const coords = this.extractCoords(plotData.geometry);
            if (coords.length === 0) return null;

            // Call use case to fetch the layer
            const layer = await this.layerFetchUseCase.execute(
                farmId,
                plotData.plotId,
                { layer: layerCode, weeksAgo, source },
            );

            const lats = coords.map((coord) => coord[0]);
            const lngs = coords.map((coord) => coord[1]);
            const bounds: [[number, number], [number, number]] = [
                [Math.min(...lats), Math.min(...lngs)],
                [Math.max(...lats), Math.max(...lngs)],
            ];

            const layerUrl = layer?.url ? `${baseUrl}${layer.url}` : null;

            if (!layerUrl) return null;

            return {
                plotId: plotData.plotId,
                layerImageUrl: layerUrl,
                bounds,
            };
        });

        // Wait for all promises to resolve and filter nulls
        const results = await Promise.all(layerPromises);
        const filteredResults = results.filter(
            (layerData): layerData is PlotLayerData => layerData !== null,
        );

        if (filteredResults.length === 0 && plotDataList.length > 0) {
            ToastManagerRepository.warning(t("farm:layers.noImageAvailable"));
        }

        return filteredResults;
    }
}
